'use client';

import React, { type JSX } from 'react';
import { IconEye } from 'icon/General/eye';
import { IconHelpCircle } from 'icon/General/help-circle';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next-nprogress-bar';
import {
  Button,
  FocusableLink,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'ui';
import { publishAtAtom } from '../../../_components/design-state';

function Container({
  children,
  kind,
}: {
  children: React.ReactNode;
  kind: 'preview' | 'freezed' | 'empty';
}) {
  return (
    <aside className={`site-status-banner site-status-banner-${kind}`}>
      {children}
    </aside>
  );
}

export function BlogStatusBanner({
  isDraftMode,
  isFreezed,
  showEmptySiteBanner,
}: {
  isDraftMode: boolean;
  isFreezed: boolean;
  showEmptySiteBanner: boolean;
}) {
  if (isFreezed) {
    return <FreezedBanner />;
  }
  if (isDraftMode) {
    return <PreviewModeBanner />;
  }
  if (showEmptySiteBanner) {
    return <EmptySiteBanner />;
  }
  return null;
}

export function PreviewModeBanner(): JSX.Element {
  const publishAt = useAtomValue(publishAtAtom);
  const isScheduled = (publishAt?.getTime() || 0) > Date.now();
  const router = useRouter();
  return (
    <Container kind="preview">
      <p className="site-status-message">
        <span className="site-status-icon">
          <IconEye />
        </span>
        <span>Preview mode</span>
        {isScheduled
          ? ` · Scheduled to publish at ${publishAt?.toLocaleString()}`
          : ''}
      </p>
      <div className="site-status-actions">
        <Button
          variant="outline"
          size="xs"
          onClick={async () => {
            await fetch(
              `/.quotion/api/realtime?url=${encodeURIComponent(location.href)}&disable=true`,
            );
            router.refresh();
          }}
          className="site-status-button"
        >
          Exit
        </Button>
      </div>
    </Container>
  );
}

export function FreezedBanner(): JSX.Element {
  return (
    <Container kind="freezed">
      <p className="site-status-message">
        Site Frozen
        <Tooltip>
          <TooltipTrigger>
            <IconHelpCircle className="size-4 shrink-0" />
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            <h3 className="mb-3 text-md font-semibold text-primary">
              Upgrade Required
            </h3>
            <p className="mb-2 text-sm text-secondary">
              You've reached the 5-note limit. Upgrade to Lite+ for unlimited
              publishing.
            </p>
            <div className="mb-3">
              <FocusableLink
                className="font-semibold text-brand-600 hover:text-brand-700 dark:hover:text-brand-500"
                href="https://app.quotion.co/billing"
              >
                Upgrade Plan
              </FocusableLink>
            </div>
            <p className="text-xs text-quaternary">
              Alternatively, remove notes to stay within the limit.
            </p>
          </TooltipContent>
        </Tooltip>
      </p>
    </Container>
  );
}

// TODO: remove this after 2025-10-24
export function EmptySiteBanner(): JSX.Element {
  return (
    <Container kind="empty">
      <p className="site-status-message">
        Empty Site
        <Tooltip>
          <TooltipTrigger aria-label="question mark">
            <IconHelpCircle className="size-4 shrink-0" />
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            <h3 className="mb-3 text-md font-semibold text-primary">
              Posts Not Showing?
            </h3>
            <div className="flex flex-col gap-2 text-sm">
              <p className="text-secondary">
                <strong>Make a change to your Quotion notes</strong> (e.g., add
                a new line) to trigger iCloud sync.
              </p>
              <p className="text-quaternary">
                After server migration, Apple requires note changes to enable
                sync. This issue has been resolved for future updates.
              </p>
              <FocusableLink
                className="font-semibold text-brand-600 hover:text-brand-700 dark:hover:text-brand-500"
                href="mailto:support@quotion.co"
              >
                Contact Support
              </FocusableLink>
            </div>
          </TooltipContent>
        </Tooltip>
      </p>
    </Container>
  );
}
